/**
 * 全局响应式 Mixin
 * 自动为组件提供响应式样式类名
 * 优化性能：使用防抖和缓存
 */
import { getDeviceType, refreshViewport } from '@/utils/viewport'
import { deviceStyles, componentClasses } from '@/config/responsive'

// 防抖函数
function debounce(fn, delay = 150) {
  let timer = null
  return function(...args) {
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => {
      fn.apply(this, args)
    }, delay)
  }
}

export default {
  data() {
    return {
      currentDeviceType: 'desktop',
      _resizeHandler: null
    }
  },
  computed: {
    // 当前设备类型
    deviceType() {
      return this.currentDeviceType
    },
    
    // 是否为移动端
    isMobileDevice() {
      return this.currentDeviceType === 'mobile'
    },
    
    // 是否为平板
    isTabletDevice() {
      return this.currentDeviceType === 'tablet'
    },
    
    // 是否为桌面端
    isDesktopDevice() {
      return this.currentDeviceType === 'desktop'
    },
    
    // 当前设备的样式配置
    deviceStyleConfig() {
      return deviceStyles[this.currentDeviceType] || deviceStyles.desktop
    },
    
    // 响应式类名生成器（缓存计算结果）
    responsiveClass() {
      const deviceType = this.currentDeviceType
      const config = this.deviceStyleConfig
      
      return {
        // 页面容器
        page: config.padding.page,
        
        // 卡片
        card: componentClasses.card[deviceType],
        
        // 按钮
        button: componentClasses.button[deviceType],
        
        // 输入框
        input: componentClasses.input[deviceType],
        
        // 标题
        h1: componentClasses.heading.h1[deviceType],
        h2: componentClasses.heading.h2[deviceType],
        h3: componentClasses.heading.h3[deviceType],
        
        // 间距
        spacing: componentClasses.spacing[deviceType],
        
        // 网格
        grid: componentClasses.grid[deviceType],
        
        // 间隙
        gap: config.gap
      }
    }
  },
  created() {
    // 在 created 钩子中初始化，避免闪烁
    this.currentDeviceType = getDeviceType()
  },
  mounted() {
    // 创建防抖的 resize 处理器
    this._resizeHandler = debounce(this.updateDeviceType.bind(this), 150)
    window.addEventListener('resize', this._resizeHandler, { passive: true })
    window.addEventListener('orientationchange', this._resizeHandler)
  },
  beforeUnmount() {
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler)
      window.removeEventListener('orientationchange', this._resizeHandler)
      this._resizeHandler = null
    }
  },
  methods: {
    updateDeviceType() {
      // 强制刷新 viewport 缓存（使用顶部 import，避免动态 require）
      refreshViewport()
      
      const newDeviceType = getDeviceType()
      // 只有设备类型真正改变时才更新，避免不必要的重渲染
      if (newDeviceType !== this.currentDeviceType) {
        this.currentDeviceType = newDeviceType
      }
    },
    
    // 获取响应式类名
    getResponsiveClass(type, size = 'md') {
      const config = componentClasses[type]
      if (!config) return ''
      
      if (typeof config === 'object' && config[this.currentDeviceType]) {
        return config[this.currentDeviceType]
      }
      
      return ''
    },
    
    // 根据设备类型返回不同的值
    getResponsiveValue(mobileValue, tabletValue, desktopValue) {
      switch (this.currentDeviceType) {
        case 'mobile':
          return mobileValue
        case 'tablet':
          return tabletValue !== undefined ? tabletValue : desktopValue
        case 'desktop':
          return desktopValue
        default:
          return desktopValue
      }
    }
  }
}
